import { z } from 'zod';

import {
  LocalizedStringSchema,
  PredicateDeclarationSchema,
  RequiredLocalizedNameSchema,
} from './predicate.js';
import { isVocabId } from './vocab-id.js';

/**
 * Schema for an opaque, minted trait id (`trait_<uuidv4>`, ADR-0004 §1).
 * Authored by the minter (`mintVocabId('trait')`), never typed by a human
 * or the LLM; decoupled from naming, never user-facing. Kept as a validated
 * string to stay JSON-serialisable through the op log.
 */
export const TraitIdSchema = z
  .string()
  .refine((s) => isVocabId('trait', s), {
    message: 'trait id must be a minted opaque id (trait_<uuidv4>) — see mintVocabId',
  });

/**
 * `EntityRoot` is the **closed** TS literal union of recognised entity
 * roots (03a-entities.md §2). II.A (#43-#47) added the five non-location
 * roots; II.B.5 (#52) finalises the union as `as const` so downstream
 * code can do exhaustive type-narrowing (`switch` on root with the
 * `never` fall-through trick).
 *
 * Closure invariants (II.B.5 / #52):
 *   - The literal tuple `ENTITY_ROOTS` is the single source of truth.
 *     `EntityRoot` is derived from it; `EntityRootSchema` is derived
 *     from it. Adding a root means appending here and updating any
 *     trait declarations that should apply to the new root
 *     (`03a-entities.md §11.5`).
 *   - The validator hard-rejects any unknown root with a structured
 *     error — both Zod's parse error and `assertEntityRoot()`'s
 *     `UnknownEntityRootError` envelope.
 *   - The CI grep test (`scripts/check-entity-root-closure.sh`) forbids
 *     any source file outside this union from emitting an entity-root
 *     string literal that is not a member of the tuple (e.g.
 *     `'walrus'`). Test fixtures may opt out with the
 *     `bypass-fixture` marker.
 *
 * NOTE: predicate.ts re-declares this tuple locally
 * (`_INTERNAL_ENTITY_ROOT_VALUES`) to avoid a circular import —
 * `pack.ts` already imports `PredicateDeclarationSchema` from
 * predicate.ts. Both tuples MUST stay in lockstep; the
 * `entity-root-closure.test.ts` diffs the two and fails on drift.
 */
export const ENTITY_ROOTS = [
  'location',
  'character',
  'faction',
  'event',
  'item',
  'lore',
] as const;
export type EntityRoot = (typeof ENTITY_ROOTS)[number];
export const EntityRootSchema = z.enum(ENTITY_ROOTS);

/**
 * Structured error returned when a caller asserts an unknown entity
 * root. The shape is stable — the auditor + validation pipeline
 * pattern-match on `kind: 'unknown_entity_root'`.
 */
export interface UnknownEntityRootError {
  readonly kind: 'unknown_entity_root';
  readonly received: string;
  readonly allowed: readonly EntityRoot[];
  readonly message: string;
}

/**
 * Hard-validating gate. Returns the value typed as `EntityRoot` when
 * it is one of the closed members; returns a structured error
 * otherwise. Use this at substrate write boundaries — the
 * closed-as-const union lets call sites downstream rely on exhaustive
 * switch narrowing.
 */
export function assertEntityRoot(
  value: unknown,
): { ok: true; value: EntityRoot } | { ok: false; error: UnknownEntityRootError } {
  if (typeof value === 'string' && (ENTITY_ROOTS as readonly string[]).includes(value)) {
    return { ok: true, value: value as EntityRoot };
  }
  return {
    ok: false,
    error: {
      kind: 'unknown_entity_root',
      received: String(value),
      allowed: ENTITY_ROOTS,
      message: `Unknown entity root "${String(value)}" — must be one of [${ENTITY_ROOTS.join(', ')}]`,
    },
  };
}

/**
 * A trait declaration — a bundle of predicates that compose onto entity
 * roots. `label`/`description` are optional human + LLM-facing strings
 * (`03a-entities.md §3`): the description grounds the AI on what the trait
 * means and surfaces as help text in the trait-attach + homebrew editors.
 *
 * The type is `z.infer`'d from the schema (not a parallel hand-written
 * interface) so optional fields carry the same `| undefined` shape the
 * schema produces under `exactOptionalPropertyTypes` — matching
 * `PredicateDeclaration`.
 */
/**
 * A facet template — a named bulk-reveal group declared by a trait
 * (`03a-entities.md §3/§11`, `30-design-system.md §7.10`). A facet groups an
 * entity's facts (by their predicates' `default_facet`) into a reveal
 * shortcut. `safe_to_bulk_reveal` gates the UX: `true` → one-click reveal
 * from the facet chip; `false` → routes through the Reveal Composer
 * (`§7.10.3`). The reveal log stays per-fact — a facet is only a UI shortcut,
 * never a unit of truth (`03 §11`).
 */
export const FacetTemplateSchema = z
  .object({
    id: z.string().min(1),
    label: LocalizedStringSchema.optional(),
    safe_to_bulk_reveal: z.boolean(),
  })
  .strict();
export type FacetTemplate = z.infer<typeof FacetTemplateSchema>;

export const TraitDeclarationSchema = z
  .object({
    /**
     * Opaque, immutable, minted id (`trait_<uuidv4>`, ADR-0004 §1).
     * Decoupled from naming; never user-facing.
     */
    id: TraitIdSchema,
    applies_to: z.array(EntityRootSchema).min(1).readonly(),
    /** Opaque predicate ids (`pred_…`) this trait bundles. */
    predicates: z.array(z.string().min(1)).readonly(),
    /**
     * **Mandatory** localized display name (ADR-0004 §1) — promoted from
     * optional now that the id is opaque. Must carry a non-empty
     * default-locale string.
     */
    label: RequiredLocalizedNameSchema,
    description: LocalizedStringSchema.optional(),
    /**
     * Bulk-reveal facet templates this trait contributes (`03a §3/§11`). A
     * predicate enrolls in one of these via its `default_facet` id. Optional;
     * a trait with no facets simply offers no bulk-reveal shortcuts.
     */
    default_facets: z.array(FacetTemplateSchema).readonly().optional(),
  })
  .strict();
export type TraitDeclaration = z.infer<typeof TraitDeclarationSchema>;

/**
 * A type definition — `(root, traits[])` plus a label (`03a-entities.md §4`).
 * A **quick-create template**, NOT a constraint: it seeds an entity with a
 * known root and a default set of traits, then the editor surfaces those
 * traits' predicate rows. The GM can still assert any predicate afterwards.
 *
 * Entity *roots* are closed (6, CI-enforced) — a type customises which roots
 * + traits combine under a friendly name; it never adds a new root. Lives in
 * a Pack like traits/predicates; the campaign-local homebrew Pack carries
 * GM-authored types via `pack.define_type` (ADR-0001). `default_facts` from
 * the design is deferred. `label`/`description` are optional (id fallback).
 */
export const TypeDefinitionSchema = z
  .object({
    id: z.string().min(1),
    root: EntityRootSchema,
    traits: z.array(z.string().min(1)).readonly(),
    label: LocalizedStringSchema.optional(),
    description: LocalizedStringSchema.optional(),
  })
  .strict();
export type TypeDefinition = z.infer<typeof TypeDefinitionSchema>;

/**
 * A pack dependency entry. Walking Skeleton shipped the bare-string
 * form (`'core'`); II.D.1 (#58) introduces the object form per
 * `03a-entities.md §11`:
 *
 *   { "pack": "core", "version": "*" }
 *
 * The version field is advisory at Phase 1 — the engine still falls
 * through `isPackVersionCompatible` for the loaded pack's own version.
 * Phase 2's pack distribution surface will tighten the constraint.
 */
export const PackDependencySchema = z.union([
  z.string().min(1),
  z
    .object({
      pack: z.string().min(1),
      version: z.string().min(1),
    })
    .strict(),
]);
export type PackDependency = z.infer<typeof PackDependencySchema>;

/**
 * A pack manifest. Carries id, version, deps, and `manifest_version` so we
 * can evolve the manifest schema later. At Walking Skeleton scope only
 * `manifest_version: 1` is recognised.
 */
export const PackManifestSchema = z
  .object({
    id: z.string().min(1),
    version: z.string().min(1),
    manifest_version: z.literal(1),
    depends_on: z.array(PackDependencySchema).readonly().optional(),
  })
  .strict();
export type PackManifest = z.infer<typeof PackManifestSchema>;

/**
 * A pack: a bundle of predicates, traits, entity roots, calendar
 * definitions, and (later) locale catalogs. The `core` pack is engine-
 * bundled JSON at Walking Skeleton scope; user-authored pack import
 * lands with the `.pack` distribution format in Phase 2
 * (03a-entities.md §11).
 *
 * `calendars` is typed loosely (`unknown[]`) at the pack-schema layer to
 * avoid a circular dependency between `@dl/shared` and `@dl/calendar`.
 * The canonical `Calendar` shape lives in `@dl/calendar` and is parsed
 * there at load time via `CalendarSchema`. Pack consumers that don't
 * need calendars can ignore the field; consumers that do (the calendar
 * runtime, the campaign settings UI) import `loadBundledCalendars()`
 * from `@dl/calendar`.
 */
export const PackSchema = z
  .object({
    manifest: PackManifestSchema,
    predicates: z.array(PredicateDeclarationSchema).readonly(),
    traits: z.array(TraitDeclarationSchema).readonly(),
    /** `(root, traits[])` quick-create templates (03a-entities.md §4). */
    types: z.array(TypeDefinitionSchema).readonly().optional(),
    entity_roots: z.array(EntityRootSchema).readonly(),
    calendars: z.array(z.unknown()).readonly().optional(),
  })
  .strict();
export type Pack = z.infer<typeof PackSchema>;

/** Stub semver compatibility checker — accepts any 0.x at WS scope. */
export function isPackVersionCompatible(_packId: string, version: string): boolean {
  return /^0\.\d+\.\d+$/.test(version) || /^0\.\d+$/.test(version);
}
